import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "@/i18n";
import api, { resolveMediaUrl } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ScrollReveal from "@/components/ScrollReveal";

const CATEGORIES = [
  { key: "all", label: { tr: "Tümü", ar: "الكل", en: "All", ru: "Все" } },
  { key: "videos", label: { tr: "Videolar", ar: "الفيديوهات", en: "Videos", ru: "Видео" } },
  { key: "mezze", label: { tr: "Mezeler", ar: "المقبلات", en: "Mezze", ru: "Мезе" } },
  { key: "kebab", label: { tr: "Kebap", ar: "كباب", en: "Kebab", ru: "Кебаб" } },
  { key: "sweets", label: { tr: "Tatlılar", ar: "الحلويات", en: "Sweets", ru: "Сладости" } },
  { key: "iftar", label: { tr: "İftar", ar: "الإفطار", en: "Iftar", ru: "Ифтар" } },
  { key: "hotel", label: { tr: "Oteller", ar: "الفنادق", en: "Hotels", ru: "Отели" } },
];

export default function Gallery() {
  const { t, tt } = useLang();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/media").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (cat === "all") return items;
    if (cat === "videos") return items.filter((i) => i.type === "video");
    return items.filter((i) => i.category === cat);
  }, [items, cat]);

  return (
    <div className="section-band">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.35em] text-brand-orange font-semibold mb-3">{t("nav.gallery")}</div>
      <h1 className="font-serif text-5xl md:text-6xl text-brand-navy" data-testid="gallery-title">{t("sections.gallery_title")}</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">{t("sections.gallery_desc")}</p>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="gallery-filters">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCat(c.key)}
            data-testid={`gallery-filter-${c.key}`}
            className={`px-4 py-2 text-sm rounded-full border transition ${cat === c.key ? "bg-brand-navy text-white border-brand-navy" : "border-brand-navy/20 text-brand-navy hover:bg-brand-navy/5"}`}
          >
            {tt(c.label)}
          </button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
        {filtered.map((m, i) => (
          <ScrollReveal key={m.id} delay={(i % 8) * 60}>
          <button
            onClick={() => setSelected(m)}
            className="media-zoom premium-card group relative aspect-square w-full rounded-xl overflow-hidden bg-brand-navy/10"
            data-testid={`gallery-item-${m.id}`}
          >
            {m.type === "video" ? (
              <>
                <video src={resolveMediaUrl(m.url)} muted loop playsInline autoPlay className="w-full h-full object-cover" />
                <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-brand-orange/95 text-white text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 backdrop-blur">
                  ● Video
                </span>
              </>
            ) : (
              <img src={resolveMediaUrl(m.url)} alt={tt(m.caption)} className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-brand-navy/80 to-transparent text-white text-xs opacity-0 group-hover:opacity-100 transition">
              {tt(m.caption)}
            </div>
          </button>
          </ScrollReveal>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-0" data-testid="gallery-lightbox">
          {selected && (
            selected.type === "video" ? (
              <video src={resolveMediaUrl(selected.url)} controls autoPlay className="w-full rounded-2xl" />
            ) : (
              <img src={resolveMediaUrl(selected.url)} alt="" className="w-full rounded-2xl" />
            )
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
